<template>
  <WhatsNewModal
    :model-value="open"
    :title="t('common.aiCompliance.title')"
    :subtitle="t('common.aiCompliance.subtitle')"
    :eyebrow="t('common.whatsNew.eyebrow')"
    :version-label="policyVersionLabel"
    :closable="!saving && !loading"
    :body-aria-label="t('common.aiCompliance.readingRegionLabel')"
    :track-scroll-end="!loading"
    :scroll-tracking-key="scrollTrackingKey"
    @update:model-value="onModalOpenChange"
    @scroll-end-change="onScrollEndChange"
  >
    <div v-if="loading" class="flex min-h-48 items-center justify-center" role="status">
      <div class="flex flex-col items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
        <span class="h-7 w-7 animate-spin rounded-full border-2 border-gray-300 border-t-[rgb(var(--tenant-brand-600)/1)] dark:border-gray-700 dark:border-t-[rgb(var(--tenant-brand-400)/1)]" aria-hidden="true" />
        {{ t('common.aiCompliance.loading') }}
      </div>
    </div>

    <div v-else class="space-y-5">
      <section class="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-950 dark:border-blue-900/70 dark:bg-blue-950/30 dark:text-blue-100">
        <h3 class="text-sm font-semibold">{{ t('common.aiCompliance.whyNowTitle') }}</h3>
        <p class="mt-1.5 text-sm leading-relaxed">
          {{ t('common.aiCompliance.regulatoryContext') }}
        </p>
      </section>

      <p class="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
        {{ t('common.aiCompliance.intro') }}
      </p>

      <ul class="space-y-3 text-sm text-gray-700 dark:text-gray-200">
        <li v-for="index in 4" :key="index" class="flex gap-3">
          <svg class="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--tenant-brand-600)/1)] dark:text-[rgb(var(--tenant-brand-400)/1)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944 11.955 11.955 0 013.382 5.984 12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>{{ t(`common.aiCompliance.rules.${index}`) }}</span>
        </li>
      </ul>

      <p class="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
        {{ t('common.aiCompliance.scopeNote') }}
      </p>

      <nav
        :aria-label="t('common.aiCompliance.resourcesLabel')"
        class="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-white/[0.03] sm:grid-cols-2"
      >
        <a
          :href="ticketBuddyAiActUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[rgb(var(--tenant-brand-700)/1)] transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--tenant-brand-600)/1)] dark:text-[rgb(var(--tenant-brand-300)/1)] dark:hover:bg-white/[0.06]"
          :aria-label="t('common.aiCompliance.articleAria')"
        >
          <svg class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M7 3.75h7.5L19 8.25v12H7v-16.5zM14 3.75v5h5M10 12h6m-6 3h6" />
          </svg>
          {{ t('common.aiCompliance.article') }}
        </a>
        <a
          :href="officialAiActUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--tenant-brand-600)/1)] dark:text-gray-200 dark:hover:bg-white/[0.06]"
          :aria-label="t('common.aiCompliance.officialInfoAria')"
        >
          <svg class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M13.5 6H18m0 0v4.5M18 6l-7.5 7.5M15 13.5V18H6V9h4.5" />
          </svg>
          {{ t('common.aiCompliance.officialInfo') }}
        </a>
      </nav>

      <div class="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <p
          id="ai-compliance-reading-status"
          role="status"
          aria-live="polite"
          class="text-xs font-medium text-gray-600 dark:text-gray-300"
        >
          {{ t(scrolledToEnd ? 'common.aiCompliance.readingComplete' : 'common.aiCompliance.scrollRequired') }}
        </p>
        <BaseCheckbox
          v-model="confirmed"
          :disabled="saving || !scrolledToEnd"
          description-id="ai-compliance-reading-status"
        >
          {{ t('common.aiCompliance.confirm') }}
        </BaseCheckbox>
      </div>

      <p v-if="error" role="alert" class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-3">
        <BaseButton variant="outline" :disabled="saving || loading" @click="cancel">
          {{ t('common.aiCompliance.later') }}
        </BaseButton>
        <BaseButton :disabled="loading || !scrolledToEnd || !confirmed || !status" :loading="saving" @click="accept">
          {{ t('common.aiCompliance.accept') }}
        </BaseButton>
      </div>
    </template>
  </WhatsNewModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import WhatsNewModal from '@/components/whats-new/WhatsNewModal.vue'
import { useAuthStore } from '@/stores/auth'
import { useProactiveDialogsStore } from '@/stores/proactive-dialogs'
import { fetchPublicAppVersion } from '@/utils/public-app-version'
import {
  OFFICIAL_AI_ACT_URL,
  ticketBuddyAiActUrl as buildTicketBuddyAiActUrl,
} from '@/utils/ai-act-links'
import {
  aiContentComplianceApi,
  type AiComplianceStatus,
} from '@/services/api/ai-content-compliance-api'
import {
  AI_COMPLIANCE_REQUIRED_EVENT,
  cancelAiComplianceAcceptance,
  resolveAiComplianceAcceptance,
} from '@/domain/ai-compliance-events'

const { locale, t } = useI18n()
const authStore = useAuthStore()
const proactiveDialogsStore = useProactiveDialogsStore()
const officialAiActUrl = OFFICIAL_AI_ACT_URL
const open = ref(false)
const confirmed = ref(false)
const scrolledToEnd = ref(false)
const saving = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
const status = ref<AiComplianceStatus | null>(null)
const appVersion = ref<string | null>(null)
let statusRequest: { staffUserId: number, promise: Promise<AiComplianceStatus> } | null = null
let lastAutomaticStaffUserId: number | null = null
let activeShowRequestId = 0
let authGeneration = 0
let activeAuthIdentity: string | null = null

const policyVersionLabel = computed(() => {
  const version = appVersion.value || status.value?.policy.version
  return version ? t('common.whatsNew.version', { version }) : ''
})
const scrollTrackingKey = computed(() => [
  loading.value ? 'loading' : 'ready',
  status.value?.policy.sha256 || 'no-policy',
  locale.value,
].join(':'))

const ticketBuddyAiActUrl = computed(() => buildTicketBuddyAiActUrl(locale.value))

async function loadStatus(staffUserId: number): Promise<AiComplianceStatus> {
  if (statusRequest?.staffUserId === staffUserId) return statusRequest.promise
  const promise = aiContentComplianceApi.getStatus()
    .finally(() => {
      if (statusRequest?.promise === promise) statusRequest = null
    })
  statusRequest = { staffUserId, promise }
  return promise
}

async function show(options: { required: boolean }): Promise<void> {
  const staffUserId = Number(authStore.user?.id)
  if (!authStore.isAuthenticated || !Number.isInteger(staffUserId) || staffUserId < 1) return
  if (!options.required && lastAutomaticStaffUserId === staffUserId) return
  if (!options.required) lastAutomaticStaffUserId = staffUserId

  const showRequestId = ++activeShowRequestId
  proactiveDialogsStore.beginAiComplianceCheck()

  confirmed.value = false
  scrolledToEnd.value = false
  error.value = null
  loading.value = true
  if (options.required) open.value = true
  try {
    const [nextStatus, currentAppVersion] = await Promise.all([
      loadStatus(staffUserId),
      fetchPublicAppVersion(),
    ])
    if (activeShowRequestId !== showRequestId || Number(authStore.user?.id) !== staffUserId) return
    status.value = nextStatus
    appVersion.value = currentAppVersion === 'unknown' ? null : currentAppVersion
    if (nextStatus.accepted) {
      open.value = false
      proactiveDialogsStore.finishAiCompliance()
      if (options.required) resolveAiComplianceAcceptance()
      return
    }
    proactiveDialogsStore.showAiCompliance()
    open.value = true
  } catch {
    if (activeShowRequestId !== showRequestId || Number(authStore.user?.id) !== staffUserId) return
    status.value = null
    if (options.required) {
      proactiveDialogsStore.showAiCompliance()
      open.value = true
      error.value = t('common.aiCompliance.loadError')
    } else {
      open.value = false
      proactiveDialogsStore.finishAiCompliance()
    }
  } finally {
    if (activeShowRequestId === showRequestId) loading.value = false
  }
}

async function accept(): Promise<void> {
  if (!status.value || !scrolledToEnd.value || !confirmed.value || saving.value) return
  const acceptanceAuthGeneration = authGeneration
  const staffUserId = Number(authStore.user?.id)
  if (!Number.isInteger(staffUserId) || staffUserId < 1) return
  saving.value = true
  error.value = null
  try {
    await aiContentComplianceApi.acceptPolicy(status.value, String(locale.value), {
      title: t('common.aiCompliance.title'),
      regulatoryContext: t('common.aiCompliance.regulatoryContext'),
      intro: t('common.aiCompliance.intro'),
      rules: Array.from({ length: 4 }, (_, index) => (
        t(`common.aiCompliance.rules.${index + 1}`)
      )),
      scopeNote: t('common.aiCompliance.scopeNote'),
      confirmation: t('common.aiCompliance.confirm'),
      eyebrow: t('common.whatsNew.eyebrow'),
      versionLabel: policyVersionLabel.value,
      scrollEndConfirmed: true,
      links: [
        {
          label: t('common.aiCompliance.article'),
          url: ticketBuddyAiActUrl.value,
        },
        {
          label: t('common.aiCompliance.officialInfo'),
          url: officialAiActUrl,
        },
      ],
    })
    if (authGeneration !== acceptanceAuthGeneration || Number(authStore.user?.id) !== staffUserId) return
    open.value = false
    proactiveDialogsStore.finishAiCompliance()
    resolveAiComplianceAcceptance()
  } catch {
    if (authGeneration !== acceptanceAuthGeneration || Number(authStore.user?.id) !== staffUserId) return
    error.value = t('common.aiCompliance.saveError')
  } finally {
    if (authGeneration === acceptanceAuthGeneration) saving.value = false
  }
}

function onScrollEndChange(reachedEnd: boolean): void {
  if (!loading.value && status.value && reachedEnd) scrolledToEnd.value = true
}

function onRequired(): void {
  void show({ required: true })
}

function cancel(): void {
  if (saving.value) return
  open.value = false
  proactiveDialogsStore.finishAiCompliance()
  cancelAiComplianceAcceptance()
}

function onModalOpenChange(value: boolean): void {
  if (value) {
    open.value = true
    return
  }
  cancel()
}

onMounted(() => window.addEventListener(AI_COMPLIANCE_REQUIRED_EVENT, onRequired))
onBeforeUnmount(() => {
  window.removeEventListener(AI_COMPLIANCE_REQUIRED_EVENT, onRequired)
  authGeneration += 1
  cancelAiComplianceAcceptance()
})

watch(
  () => [authStore.isAuthenticated, authStore.user?.id] as const,
  ([authenticated, staffUserId]) => {
    const nextAuthIdentity = authenticated && staffUserId ? String(staffUserId) : null
    if (nextAuthIdentity !== activeAuthIdentity) {
      activeAuthIdentity = nextAuthIdentity
      authGeneration += 1
      activeShowRequestId += 1
      statusRequest = null
      saving.value = false
      open.value = false
      status.value = null
      appVersion.value = null
      cancelAiComplianceAcceptance()
    }
    if (!authenticated || !staffUserId) {
      lastAutomaticStaffUserId = null
      proactiveDialogsStore.reset()
      return
    }
    void show({ required: false })
  },
  { immediate: true },
)

watch(locale, () => {
  if (!open.value) return
  confirmed.value = false
  scrolledToEnd.value = false
})
</script>
